import {
  buildCylinderGeometry,
  buildSphereGeometry,
  Mesh,
  Node,
  PhongMaterial,
  type TickEvent,
  VBOGeometry,
  Viewer,
} from '@xeokit/xeokit-sdk';
import { collectObjectTypes } from '~/utils/viewer/filters/collect-properties';

const BODY_HEIGHT = 1.8;
const BODY_RADIUS = 0.3;
const EYE_HEIGHT = 1.7;
const WALK_SPEED = 1.6;
const RUN_SPEED = 4.5;
const LOOK_SENSITIVITY = 0.0025;
const MAX_PITCH = Math.PI / 2 - 0.05;
const MAX_LOOK_DELTA = 100;
const GRAVITY = 9.81;
const STEP_HEIGHT = 0.6;
const MAX_FRAME_SECONDS = 0.1;
const SHOW_BODY = false;
const JETPACK_ACCELERATION = 18;
const JETPACK_MAX_SPEED = 4;
const FLY_SPEED = 5;
const FLY_FAST_SPEED = 14;
const DOOR_TYPES = new Set(['ifcdoor', 'doors']);
const TARGET_REACH = 2.5;
const TARGET_SCAN_INTERVAL = 0.15;
const SNAP_INTERVAL = 80;
const SNAP_REACH = 8;
const OFFSET_EPSILON = 0.002;
const HELD_SMOOTHING = 14;
const DOOR_PASS_MARGIN = 0.6;

export type WalkModeTarget = 'door' | 'hide' | 'inspect' | 'move' | 'place' | null;

const MOVE_KEYS = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'Space',
  'ShiftLeft',
  'ShiftRight',
  'ControlLeft',
  'ControlRight',
]);
const OBSTRUCTING_TYPES = new Set(['ifcspace', 'ifcopeningelement', 'ifcannotation', 'rooms']);
const LEGEND_KEYS = new Map([
  ['KeyW', 'W'],
  ['KeyA', 'A'],
  ['KeyS', 'S'],
  ['KeyD', 'D'],
  ['ShiftLeft', 'Shift'],
  ['ShiftRight', 'Shift'],
  ['Space', 'Space'],
  ['ControlLeft', 'Ctrl'],
  ['ControlRight', 'Ctrl'],
  ['KeyF', 'F'],
  ['KeyG', 'G'],
  ['KeyI', 'I'],
  ['KeyM', 'M'],
  ['KeyN', 'N'],
  ['KeyP', 'P'],
  ['KeyQ', 'Q'],
  ['KeyR', 'R'],
]);

function isTypingTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement
  );
}

export class WalkMode {
  readonly viewer: Viewer;

  private readonly onTarget: (target: WalkModeTarget) => void;
  private readonly onHideMode: (active: boolean) => void;
  private readonly onMoveMode: (active: boolean) => void;
  private readonly onFlying: (active: boolean) => void;
  private readonly onSnap: (active: boolean) => void;
  private readonly onInspectMode: (active: boolean) => void;
  private readonly onInspectPick: (entityId: string) => void;
  private readonly onPressedKeys: (keys: string[]) => void;
  private readonly onToggleMinimap: () => void;
  private readonly onExit: () => void;
  private readonly position = [0, 0, 0];
  private readonly pressedKeys = new Set<string>();
  private body: Node | undefined;
  private tickSubId: string | undefined;
  private cullGuardSubId: string | undefined;
  private listeners: AbortController | undefined;
  private typeByEntityId = new Map<string, string>();
  private hiddenEntityIds: string[] = [];
  private manuallyHiddenIds: string[] = [];
  private movedEntityIds = new Set<string>();
  private groundLevel = 0;
  private targetEntityId: string | undefined;
  private targetPoint: number[] | undefined;
  private hideMode = false;
  private moveMode = false;
  private inspectMode = false;
  private heldEntityId: string | undefined;
  private heldStartOffset = [0, 0, 0];
  private heldGrabPoint = [0, 0, 0];
  private heldBaseAabb = [0, 0, 0, 0, 0, 0];
  private heldOffset = [0, 0, 0];
  private heldDistance = 0;
  private snapPlacement: { point: number[]; normal: number[] } | undefined;
  private snapEnabled = true;
  private snapDue = 0;
  private flying = false;
  private targetScanDue = 0;
  private viewChanged = true;
  private yaw = 0;
  private pitch = 0;
  private verticalSpeed = 0;
  private grounded = true;
  private active = false;

  constructor(
    viewer: Viewer,
    handlers: {
      onTarget?: (target: WalkModeTarget) => void;
      onHideMode?: (active: boolean) => void;
      onMoveMode?: (active: boolean) => void;
      onFlying?: (active: boolean) => void;
      onSnap?: (active: boolean) => void;
      onInspectMode?: (active: boolean) => void;
      onInspectPick?: (entityId: string) => void;
      onPressedKeys?: (keys: string[]) => void;
      onToggleMinimap?: () => void;
      onExit?: () => void;
    } = {},
  ) {
    this.viewer = viewer;
    this.onTarget = handlers.onTarget ?? (() => {});
    this.onHideMode = handlers.onHideMode ?? (() => {});
    this.onMoveMode = handlers.onMoveMode ?? (() => {});
    this.onFlying = handlers.onFlying ?? (() => {});
    this.onSnap = handlers.onSnap ?? (() => {});
    this.onInspectMode = handlers.onInspectMode ?? (() => {});
    this.onInspectPick = handlers.onInspectPick ?? (() => {});
    this.onPressedKeys = handlers.onPressedKeys ?? (() => {});
    this.onToggleMinimap = handlers.onToggleMinimap ?? (() => {});
    this.onExit = handlers.onExit ?? (() => {});
  }

  activate() {
    if (this.active) return;
    this.active = true;

    const camera = this.viewer.camera;
    const eye = camera.eye;
    const look = camera.look;
    this.yaw = Math.atan2(look[1] - eye[1], look[0] - eye[0]);
    this.pitch = 0;
    this.verticalSpeed = 0;
    this.grounded = false;
    this.typeByEntityId = collectObjectTypes(this.viewer);
    this.hideObstructingObjects();
    this.groundLevel = this.resolveGroundLevel();

    this.position[0] = look[0];
    this.position[1] = look[1];
    this.position[2] = Math.max(this.groundLevel, look[2]);

    this.createBody();

    this.viewer.cameraControl.active = false;
    camera.up = [0, 0, 1];

    this.listeners = new AbortController();
    const signal = this.listeners.signal;

    this.getCanvas().addEventListener('mousedown', this.lockPointer, { signal });
    this.getCanvas().addEventListener('mousedown', this.handlePrimaryDown, { signal });
    this.getCanvas().addEventListener('wheel', this.forwardScroll, { signal });
    document.addEventListener('mouseup', this.releaseHeld, { signal });
    document.addEventListener('mousemove', this.handleMouseMove, { signal });
    window.addEventListener('keydown', this.handleKeyDown, { signal });
    window.addEventListener('keyup', this.handleKeyUp, { signal });
    window.addEventListener('blur', this.releaseKeys, { signal });
    document.addEventListener('pointerlockchange', this.handlePointerLockChange, { signal });

    this.tickSubId = this.viewer.scene.on('tick', this.handleTick);

    this.syncCamera();
    this.lockPointer();
  }

  deactivate() {
    if (!this.active) return;
    this.active = false;

    this.listeners?.abort();
    this.listeners = undefined;
    this.releaseKeys();

    if (this.tickSubId !== undefined) {
      this.viewer.scene.off(this.tickSubId);
      this.tickSubId = undefined;
    }

    if (document.pointerLockElement) document.exitPointerLock();

    this.setTarget(undefined);

    this.releaseHeld();

    if (this.hideMode) {
      this.hideMode = false;
      this.onHideMode(false);
    }

    if (this.moveMode) this.setMoveMode(false);
    if (this.inspectMode) this.setInspectMode(false);
    if (this.flying) this.setFlying(false);

    this.viewer.scene.setObjectsVisible(this.hiddenEntityIds, true);
    this.hiddenEntityIds = [];
    this.typeByEntityId = new Map();

    this.body?.destroy();
    this.body = undefined;

    this.viewer.cameraControl.active = true;
  }

  private createBody() {
    const body = new Node(this.viewer.scene, {
      position: [...this.position],
      rotation: [90, 0, 0],
      visible: SHOW_BODY,
      collidable: false,
      colorize: [1, 1, 1],
      opacity: 1,
    });

    const material = new PhongMaterial(body, {
      diffuse: [0.15, 0.45, 0.85],
      emissive: [0.05, 0.15, 0.3],
    });

    const cap = () => buildSphereGeometry({ radius: BODY_RADIUS, heightSegments: 12 });
    const parts = [
      {
        geometry: buildCylinderGeometry({
          radiusTop: BODY_RADIUS,
          radiusBottom: BODY_RADIUS,
          height: BODY_HEIGHT - 2 * BODY_RADIUS,
          radialSegments: 24,
        }),
        height: BODY_HEIGHT / 2,
      },
      { geometry: cap(), height: BODY_RADIUS },
      { geometry: cap(), height: BODY_HEIGHT - BODY_RADIUS },
    ];

    for (const part of parts) {
      body.addChild(
        new Mesh(body, {
          geometry: new VBOGeometry(body, part.geometry),
          material,
          position: [0, part.height, 0],
          visible: SHOW_BODY,
          pickable: false,
          clippable: false,
          collidable: false,
        }),
        false,
      );
    }

    this.body = body;
  }

  private castRay(origin: number[], direction: number[]) {
    return this.viewer.scene.pick({
      origin,
      direction,
      pickSurface: true,
      pickSurfaceNormal: false,
    })?.worldPos;
  }

  private castDown(fromZ: number) {
    return this.castRay([this.position[0], this.position[1], fromZ], [0, 0, -1])?.[2];
  }

  private moveAxis(axis: 0 | 1, distance: number) {
    const reach = BODY_RADIUS + Math.abs(distance);
    const direction = [0, 0, 0];
    direction[axis] = Math.sign(distance);

    const hit = this.castRay(
      [this.position[0], this.position[1], this.position[2] + BODY_HEIGHT / 2],
      direction,
    );

    if (hit && Math.abs(hit[axis] - this.position[axis]) <= reach) return;

    this.position[axis] += distance;
  }

  private resolveGroundLevel() {
    const scene = this.viewer.scene;
    const aabb = scene.getAABB(scene.visibleObjectIds);

    return Number.isFinite(aabb?.[2]) ? aabb[2] : 0;
  }

  private entityType(entityId: string) {
    // @ts-ignore xeokit sdk
    const metaObjects = this.viewer.metaScene?.metaObjects ?? {};
    const originalSystemId = String(this.viewer.scene.objects[entityId]?.originalSystemId);

    const type =
      this.typeByEntityId.get(entityId) ??
      metaObjects[entityId]?.type ??
      metaObjects[originalSystemId]?.type;

    return typeof type === 'string' ? type.toLowerCase() : '';
  }

  private hideObstructingObjects() {
    const objects = this.viewer.scene.objects;

    this.hiddenEntityIds = Object.keys(objects).filter(
      (entityId) => objects[entityId].visible && OBSTRUCTING_TYPES.has(this.entityType(entityId)),
    );

    this.viewer.scene.setObjectsVisible(this.hiddenEntityIds, false);
  }

  private setTarget(entityId: string | undefined) {
    if (entityId === this.targetEntityId) return;

    const previous = this.targetEntityId && this.viewer.scene.objects[this.targetEntityId];
    if (previous) previous.highlighted = false;

    const next = entityId && this.viewer.scene.objects[entityId];
    if (next) next.highlighted = true;

    this.targetEntityId = entityId;
    this.onTarget(
      entityId
        ? this.moveMode
          ? 'move'
          : this.hideMode
            ? 'hide'
            : this.inspectMode
              ? 'inspect'
              : 'door'
        : null,
    );
  }

  private handlePrimaryDown = (event: MouseEvent) => {
    if (event.button !== 0) return;

    if (this.inspectMode) {
      if (this.targetEntityId) this.onInspectPick(this.targetEntityId);
      return;
    }

    if (this.hideMode) {
      this.hideTarget();
      return;
    }

    if (!this.moveMode || this.heldEntityId) return;

    const entityId = this.targetEntityId;
    const entity = entityId && this.viewer.scene.objects[entityId];
    const grabPoint = this.targetPoint;
    if (!entityId || !entity || !grabPoint) return;

    const eye = this.viewer.camera.eye;

    this.heldEntityId = entityId;
    this.movedEntityIds.add(entityId);
    this.guardMovedVisibility();
    this.heldStartOffset = [...(entity.offset ?? [0, 0, 0])];
    this.heldBaseAabb = [...entity.aabb];
    this.heldGrabPoint = grabPoint;
    this.heldOffset = [...this.heldStartOffset];
    this.snapPlacement = undefined;
    this.snapDue = 0;
    this.heldDistance = Math.hypot(
      grabPoint[0] - eye[0],
      grabPoint[1] - eye[1],
      grabPoint[2] - eye[2],
    );

    entity.pickable = false;
    this.onTarget('place');
  };

  private releaseHeld = () => {
    const entity = this.heldEntityId && this.viewer.scene.objects[this.heldEntityId];
    if (!entity) return;

    this.snapDue = 0;
    this.updateHeldObject();

    entity.pickable = true;
    this.heldEntityId = undefined;

    this.viewChanged = true;
    this.targetScanDue = 0;
    this.onTarget(this.targetEntityId ? 'move' : null);
  };

  private updateHeldObject(seconds?: number) {
    const entity = this.heldEntityId && this.viewer.scene.objects[this.heldEntityId];
    if (!entity) return;

    const camera = this.viewer.camera;
    const eye = camera.eye;
    const look = camera.look;
    const length = Math.hypot(look[0] - eye[0], look[1] - eye[1], look[2] - eye[2]) || 1;

    const snap = this.snapSurface();
    const base = this.heldBaseAabb;
    const halfExtent = (axis: number) => (base[axis + 3] - base[axis]) / 2;
    const push = snap
      ? [0, 1, 2].reduce((sum, axis) => sum + Math.abs(snap.normal[axis]) * halfExtent(axis), 0)
      : 0;

    const target = snap
      ? [0, 1, 2].map(
          (axis) => snap.point[axis] + snap.normal[axis] * push - (base[axis] + base[axis + 3]) / 2,
        )
      : [0, 1, 2].map(
          (axis) =>
            this.heldStartOffset[axis] +
            eye[axis] +
            ((look[axis] - eye[axis]) / length) * this.heldDistance -
            this.heldGrabPoint[axis],
        );

    const blend = seconds === undefined ? 1 : 1 - Math.exp(-HELD_SMOOTHING * seconds);
    const offset = [0, 1, 2].map(
      (axis) => this.heldOffset[axis] + (target[axis] - this.heldOffset[axis]) * blend,
    );

    if (offset.every((value, axis) => Math.abs(value - this.heldOffset[axis]) < OFFSET_EPSILON)) {
      return;
    }

    this.heldOffset = offset;
    entity.offset = offset;
  }

  private setInspectMode(active: boolean) {
    if (active) {
      if (this.hideMode) this.toggleHideMode();
      if (this.moveMode) this.setMoveMode(false);
    }

    this.inspectMode = active;
    this.onInspectMode(active);

    this.setTarget(undefined);
    this.viewChanged = true;
    this.targetScanDue = 0;
  }

  private forwardScroll = (event: WheelEvent) => {
    if (!this.inspectMode) return;

    document
      .querySelector('[data-saved-view-scroll="properties"]')
      ?.scrollBy({ top: event.deltaY });
  };

  private toggleSnap() {
    this.snapEnabled = !this.snapEnabled;
    this.snapPlacement = undefined;
    this.snapDue = 0;
    this.onSnap(this.snapEnabled);
  }

  private snapSurface() {
    if (!this.snapEnabled) return undefined;

    const now = performance.now();
    if (now < this.snapDue) return this.snapPlacement;

    this.snapDue = now + SNAP_INTERVAL;

    const camera = this.viewer.camera;
    const eye = camera.eye;
    const look = camera.look;
    const length = Math.hypot(look[0] - eye[0], look[1] - eye[1], look[2] - eye[2]) || 1;
    const direction = [0, 1, 2].map((axis) => (look[axis] - eye[axis]) / length);

    const hit = this.viewer.scene.pick({
      origin: [...eye],
      direction,
      pickSurface: true,
    });
    const worldPos = hit?.worldPos;
    const distance = worldPos
      ? Math.hypot(worldPos[0] - eye[0], worldPos[1] - eye[1], worldPos[2] - eye[2])
      : Infinity;

    this.snapPlacement =
      worldPos && distance <= SNAP_REACH
        ? { point: [...worldPos], normal: this.facingNormal(hit?.worldNormal, direction) }
        : undefined;

    return this.snapPlacement;
  }

  private facingNormal(worldNormal: number[] | undefined | null, rayDirection: number[]) {
    const length = worldNormal ? Math.hypot(worldNormal[0], worldNormal[1], worldNormal[2]) : 0;
    if (!worldNormal || length < 1e-6) return [0, 0, 1];

    const sign =
      worldNormal.reduce((dot, value, axis) => dot + value * rayDirection[axis], 0) > 0 ? -1 : 1;

    return worldNormal.map((value) => (sign * value) / length);
  }

  private setMoveMode(active: boolean) {
    if (active && this.viewer.scene.dtxEnabled) return;

    if (active && this.inspectMode) this.setInspectMode(false);

    this.moveMode = active;
    if (active && this.hideMode) this.toggleHideMode();
    if (!active) this.releaseHeld();

    this.onMoveMode(active);

    this.setTarget(undefined);
    this.viewChanged = true;
    this.targetScanDue = 0;
  }

  private setFlying(active: boolean) {
    this.flying = active;
    this.verticalSpeed = 0;
    this.grounded = false;
    this.onFlying(active);
  }

  private toggleHideMode() {
    this.hideMode = !this.hideMode;
    if (this.hideMode && this.inspectMode) this.setInspectMode(false);
    if (this.hideMode && this.moveMode) this.setMoveMode(false);
    this.onHideMode(this.hideMode);

    this.setTarget(undefined);
    this.viewChanged = true;
    this.targetScanDue = 0;
  }

  private scanForTarget(seconds: number) {
    if (!this.viewChanged || this.heldEntityId) return;

    this.targetScanDue -= seconds;
    if (this.targetScanDue > 0) return;

    this.targetScanDue = TARGET_SCAN_INTERVAL;
    this.viewChanged = false;

    const canvas = this.getCanvas();
    const hit = this.viewer.scene.pick({
      canvasPos: [canvas.clientWidth / 2, canvas.clientHeight / 2],
      pickSurface: true,
    });

    const entityId = hit?.entity?.id as string | undefined;
    const eyeZ = this.position[2] + EYE_HEIGHT;
    const distance = hit?.worldPos
      ? Math.hypot(
          hit.worldPos[0] - this.position[0],
          hit.worldPos[1] - this.position[1],
          hit.worldPos[2] - eyeZ,
        )
      : Infinity;

    const inReach = !!entityId && distance <= TARGET_REACH;
    const interactive =
      inReach &&
      (this.moveMode ||
        this.hideMode ||
        this.inspectMode ||
        (!this.flying && DOOR_TYPES.has(this.entityType(entityId as string))));

    this.targetPoint = interactive && hit?.worldPos ? [...hit.worldPos] : undefined;
    this.setTarget(interactive ? entityId : undefined);
  }

  private hideTarget() {
    const entityId = this.targetEntityId;
    const target = entityId && this.viewer.scene.objects[entityId];
    if (!entityId || !target) return;

    this.setTarget(undefined);
    target.visible = false;
    this.manuallyHiddenIds.push(entityId);
  }

  private resetChanges() {
    this.viewer.scene.setObjectsVisible(this.manuallyHiddenIds, true);
    this.manuallyHiddenIds = [];

    for (const entityId of this.movedEntityIds) {
      const entity = this.viewer.scene.objects[entityId];
      if (entity) entity.offset = [0, 0, 0];
    }
    this.movedEntityIds.clear();
    this.releaseCullGuard();

    this.setTarget(undefined);
    this.viewChanged = true;
    this.targetScanDue = 0;
  }

  private guardMovedVisibility() {
    if (this.cullGuardSubId !== undefined) return;

    this.cullGuardSubId = this.viewer.scene.on('tick', this.keepMovedVisible);
  }

  private releaseCullGuard() {
    if (this.cullGuardSubId === undefined) return;

    this.viewer.scene.off(this.cullGuardSubId);
    this.cullGuardSubId = undefined;
  }

  private keepMovedVisible = () => {
    for (const entityId of this.movedEntityIds) {
      const entity = this.viewer.scene.objects[entityId];
      if (entity?.culled) entity.culled = false;
    }
  };

  private enterDoor() {
    if (this.flying) return;

    const entityId = this.targetEntityId;
    const door = entityId && this.viewer.scene.objects[entityId];
    if (!door || !DOOR_TYPES.has(this.entityType(entityId))) return;

    const doorOffset = door.offset ?? [0, 0, 0];
    const aabb = [...door.aabb].map((value, index) => value + doorOffset[index % 3]);
    const dirX = Math.cos(this.yaw);
    const dirY = Math.sin(this.yaw);
    const reach =
      (Math.abs(dirX) * (aabb[3] - aabb[0]) + Math.abs(dirY) * (aabb[4] - aabb[1])) / 2 +
      BODY_RADIUS +
      DOOR_PASS_MARGIN;

    this.position[0] = (aabb[0] + aabb[3]) / 2 + dirX * reach;
    this.position[1] = (aabb[1] + aabb[4]) / 2 + dirY * reach;
    this.position[2] = Math.max(this.groundLevel, aabb[2]);
    this.verticalSpeed = 0;
    this.grounded = false;

    this.setTarget(undefined);
    this.syncCamera();
  }

  private applyGravity(seconds: number) {
    this.verticalSpeed -= GRAVITY * seconds;

    const floorLevel = this.castDown(this.position[2] + STEP_HEIGHT);
    const ground = Math.max(this.groundLevel, floorLevel ?? this.groundLevel);
    const nextZ = this.position[2] + this.verticalSpeed * seconds;

    if (nextZ <= ground) {
      this.position[2] = ground;
      this.verticalSpeed = 0;
      this.grounded = true;
    } else {
      this.position[2] = nextZ;
      this.grounded = false;
    }
  }

  private syncCamera() {
    const camera = this.viewer.camera;
    const cosPitch = Math.cos(this.pitch);
    const eye = [this.position[0], this.position[1], this.position[2] + EYE_HEIGHT];

    camera.eye = eye;
    camera.look = [
      eye[0] + cosPitch * Math.cos(this.yaw),
      eye[1] + cosPitch * Math.sin(this.yaw),
      eye[2] + Math.sin(this.pitch),
    ];

    this.viewChanged = true;

    if (this.body) this.body.position = [...this.position];
  }

  private handleTick = (tickEvent: TickEvent) => {
    const seconds = Math.min(Number(tickEvent.deltaTime) / 1000, MAX_FRAME_SECONDS);

    this.scanForTarget(seconds);

    if (this.heldEntityId) this.updateHeldObject(seconds);

    const forward = (this.pressedKeys.has('KeyW') ? 1 : 0) - (this.pressedKeys.has('KeyS') ? 1 : 0);
    const strafe = (this.pressedKeys.has('KeyD') ? 1 : 0) - (this.pressedKeys.has('KeyA') ? 1 : 0);
    const ascending = this.pressedKeys.has('Space');
    const descending = this.pressedKeys.has('ControlLeft') || this.pressedKeys.has('ControlRight');
    const running = this.pressedKeys.has('ShiftLeft') || this.pressedKeys.has('ShiftRight');
    const resting = this.grounded && this.verticalSpeed === 0;

    if (this.flying) {
      const speed = running ? FLY_FAST_SPEED : FLY_SPEED;
      this.flyStep(seconds, forward, strafe, (ascending ? 1 : 0) - (descending ? 1 : 0), speed);
      return;
    }

    if (!forward && !strafe && !ascending && resting) return;

    if (forward || strafe) {
      const step = ((running ? RUN_SPEED : WALK_SPEED) * seconds) / Math.hypot(forward, strafe);
      const sin = Math.sin(this.yaw);
      const cos = Math.cos(this.yaw);

      this.moveAxis(0, (cos * forward + sin * strafe) * step);
      this.moveAxis(1, (sin * forward - cos * strafe) * step);
    }

    if (ascending) {
      this.verticalSpeed = Math.min(
        this.verticalSpeed + JETPACK_ACCELERATION * seconds,
        JETPACK_MAX_SPEED,
      );
    }

    this.applyGravity(seconds);
    this.syncCamera();
  };

  private flyStep(seconds: number, forward: number, strafe: number, lift: number, speed: number) {
    if (!forward && !strafe && !lift) return;

    if (forward || strafe) {
      const step = (speed * seconds) / Math.hypot(forward, strafe);
      const sin = Math.sin(this.yaw);
      const cos = Math.cos(this.yaw);
      const cosPitch = Math.cos(this.pitch);

      this.position[0] += (cos * forward * cosPitch + sin * strafe) * step;
      this.position[1] += (sin * forward * cosPitch - cos * strafe) * step;
      this.position[2] += Math.sin(this.pitch) * forward * step;
    }

    this.position[2] += lift * speed * seconds;

    this.syncCamera();
  }

  private handleMouseMove = (event: MouseEvent) => {
    if (document.pointerLockElement !== this.getCanvas()) return;

    const clamp = (value: number, limit: number) => Math.max(-limit, Math.min(limit, value));

    this.yaw -= clamp(event.movementX, MAX_LOOK_DELTA) * LOOK_SENSITIVITY;
    this.pitch = clamp(
      this.pitch - clamp(event.movementY, MAX_LOOK_DELTA) * LOOK_SENSITIVITY,
      MAX_PITCH,
    );

    this.syncCamera();
  };

  private handlePointerLockChange = () => {
    if (!this.active || document.pointerLockElement) return;

    this.onExit();
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey || event.altKey || isTypingTarget(event.target)) return;
    if (event.ctrlKey && !MOVE_KEYS.has(event.code)) return;

    if (LEGEND_KEYS.has(event.code) && !this.pressedKeys.has(event.code)) {
      this.pressedKeys.add(event.code);
      this.emitPressedKeys();
    }

    if (event.code === 'Escape') {
      this.onExit();
      return;
    }

    if (event.code === 'KeyM') {
      if (!event.repeat) this.onToggleMinimap();
      event.preventDefault();
      return;
    }

    if (event.code === 'KeyF' || event.code === 'KeyQ') {
      if (!event.repeat) {
        if (event.code === 'KeyQ') this.toggleHideMode();
        else this.enterDoor();
      }
      event.preventDefault();
      return;
    }

    if (event.code === 'KeyG') {
      if (!event.repeat) this.setFlying(!this.flying);
      event.preventDefault();
      return;
    }

    if (event.code === 'KeyP') {
      if (!event.repeat) this.setMoveMode(!this.moveMode);
      event.preventDefault();
      return;
    }

    if (event.code === 'KeyI') {
      if (!event.repeat) this.setInspectMode(!this.inspectMode);
      event.preventDefault();
      return;
    }

    if (event.code === 'KeyN') {
      if (!event.repeat) this.toggleSnap();
      event.preventDefault();
      return;
    }

    if (event.code === 'KeyR') {
      if (!event.repeat) this.resetChanges();
      event.preventDefault();
      return;
    }

    if (!MOVE_KEYS.has(event.code)) return;

    event.preventDefault();
  };

  private handleKeyUp = (event: KeyboardEvent) => {
    if (!this.pressedKeys.delete(event.code)) return;

    this.emitPressedKeys();
  };

  private releaseKeys = () => {
    if (!this.pressedKeys.size) return;

    this.pressedKeys.clear();
    this.emitPressedKeys();
  };

  private emitPressedKeys() {
    const labels = new Set<string>();

    for (const code of this.pressedKeys) {
      const label = LEGEND_KEYS.get(code);
      if (label) labels.add(label);
    }

    this.onPressedKeys([...labels]);
  }

  private lockPointer = () => {
    void Promise.resolve(this.getCanvas().requestPointerLock()).catch(() => {});
  };

  private getCanvas() {
    // @ts-ignore xeokit
    return this.viewer.scene.canvas.canvas as HTMLCanvasElement;
  }
}
